import productData from '@/js/productData'
import utils from '@/js/utils'
import store from '@/store'
import MasterPricing from '@/js/pricing/MasterPricing'
import { resolveWorktop } from '@/js/WorktopResolver'

const mmPerFoot = 304.8
const sqMmPerSqFoot = 92903.04

export default {
  fronts(items, materials, handleOptions) {
    let legacyHandle = false

    let o = {
      cost: 0,
      total: 0,
      incomplete: false,
      notifications: [],
      details: []
    }

    if (!items || items.length === 0 || !materials || materials.length === 0) {
      return o
    }

    let numHandles = 0
    let numHandlesWithJ = 0
    let handleArea = 0 // used to work out sheet usage for handles (for material ordering) - BUT NOT CHARGED!
    let handlesPerimeterFt = 0
    let jHandlesPerimeterFt = 0

    for (let material of materials) {
      // ignore any incomplete materials
      if (!material.value || !material.w || !material.h || !material.thickness) continue

      let area = 0
      let perimeter = 0
      let itemCount = 0
      let handle = null
      let materialHandlesCost = 0 // handle cost for this material (only)
      let materialLucidHandlesCost = 0 // lucid handle cost for this material (only)
      let edgeBandingCost = 0

      // FRONT ITEMS (18mm)
      let frontsUsingMaterial = items.filter((item) => {
        return item.material === material.id
      })

      for (let item of frontsUsingMaterial) {
        // ignore any imcomplete items
        if (
          item.itemNum == undefined ||
          item.w == undefined ||
          item.h == undefined ||
          item.qty == undefined ||
          (!item.coverPanelType && item.handle == undefined)
        ) {
          o.incomplete.push('Front: ' + item.itemNum)
          continue
        }

        //v.plinths incomplete
        if (
          item.coverPanelType === 'v-plinth' &&
          (item.itemNum == undefined ||
            item.w == undefined ||
            item.h == undefined ||
            item.qty == undefined ||
            item.vent == undefined ||
            item.ventRef == undefined ||
            item.ventOffset == undefined)
        ) {
          o.incomplete = true
          continue
        }

        const width = parseInt(item.w)
        const height = parseInt(item.h)
        //change qty for corner doors
        const qty = parseInt(item.qty)

        let frontTypeArray =
          item.frontType && item.frontType.includes('-') ? item.frontType.split('-') : [item.frontType]
        let hingeType = frontTypeArray[0]
        let cabinetType = frontTypeArray[1]
        let specialType = frontTypeArray.length == 3 ? frontTypeArray[2] : null

        // change area calc for corner doors
        const cornerNarrowFillerWidth = cabinetType && cabinetType == 'w' ? 102 : 51
        const cornerWideFillerWidth = cabinetType && cabinetType == 'w' ? 102 : 70
        area +=
          specialType == 'cnr'
            ? width * height + cornerNarrowFillerWidth * height + cornerWideFillerWidth * height
            : width * height * qty
        itemCount += qty

        // find handle and make it global
        if (item.handle) {
          // Guard against null handleOptions
          if (!handleOptions) continue

          handle = handleOptions.find((handle) => {
            return handle.id == item.handle
          })
        }

        if (item.frontType) {
          // adding LINEAR FEET from door features

          const hingeLinearMM = 161.79
          const drwHoleLinearMM = 31.42
          const fourHingeDoorDimensions = { w: 606.6, h: 1521 }
          const tenHoleDrwDimensions = { w: 378, h: 759 } // check if width has minus shadowGap
          const centerHingeThreshold = 1267

          // set basic perimeter from width + height of door
          perimeter +=
            specialType == '2part'
              ? (width + height) * 2 * 2 * qty
              : specialType == 'cnr'
              ? (width + height) * 2 + (cornerNarrowFillerWidth + height) * 2 + (cornerWideFillerWidth + height) * 2
              : (width + height) * 2 * qty

          if (!['pax', 'paxsplit'].includes(specialType)) {
            if (hingeType.includes('hd')) {
              if (height < centerHingeThreshold) {
                if (!['custom', 'a', '2part', 'cnr'].includes(specialType)) {
                  perimeter += hingeLinearMM * 2 * qty
                } else if (specialType == '2part') {
                  perimeter += hingeLinearMM * 2 * 2 * qty
                } else if (specialType == 'cnr') {
                  perimeter += hingeLinearMM * 2
                }
              } else if (height >= centerHingeThreshold) {
                if (height == fourHingeDoorDimensions.h && width == fourHingeDoorDimensions.w) {
                  perimeter += hingeLinearMM * 4 * qty
                } else {
                  perimeter += hingeLinearMM * 3 * qty
                }
                // super tall doors has extra 2 hinges
                if ((width == 606.6 && height == 1521) || height == 1797 || height == 1997) {
                  perimeter += hingeLinearMM * 2 * qty
                }
              }
            } else if (hingeType.includes('drw')) {
              let holeCount = null
              if (width != tenHoleDrwDimensions.w) {
                if (height <= 100) {
                  holeCount = 2
                } else if (height <= 165.1) {
                  holeCount = 4
                } else if (height <= 212.725) {
                  holeCount = 5
                } else {
                  holeCount = 6
                }
              } else {
                if (height == tenHoleDrwDimensions.h) {
                  holeCount = 10
                }
              }
              perimeter += holeCount * drwHoleLinearMM * 2 * qty
            }
          } else {
            // US pax
            let hingeCount = null
            if (specialType == 'pax') {
              switch (height) {
                case 1944:
                case 1999:
                  hingeCount = 3
                  break
                case 2296:
                case 2351:
                  hingeCount = 4
                  break
              }
            } else if (specialType == 'paxsplit') {
              switch (height) {
                case 861:
                case 925:
                case 916:
                case 980:
                  hingeCount = 1
                  break
                case 1080:
                  hingeCount = 2
                  break
                case 1368:
                  hingeCount = 3
                  break
              }
            }
            perimeter += hingeCount * hingeLinearMM * qty
          }

          // joined - add metal bracket linear mm if not j-prof
          if (item?.joiningPos && handle.value != 'j') {
            perimeter += 223.4 * qty
          }

          // also add avg engraving (in mm)
          perimeter += 20 * qty
        } else if (item.coverPanelType) {
          perimeter += (width + height) * 2 * qty
        }

        // Edge banding cost for cover panels
        if (item.coverPanelType && item.edgeMode === 'edgeBanding' && item.edges) {
          if (item.edges.top) edgeBandingCost += 50 * qty
          if (item.edges.bottom) edgeBandingCost += 50 * qty
          if (item.edges.left) edgeBandingCost += 50 * qty
          if (item.edges.right) edgeBandingCost += 50 * qty
        }

        if (handle && item.handle) {
          let masterHandle = MasterPricing.getHandle(handle.value)
          let handleQty = specialType == 'cnr' ? 1 : qty

          if (masterHandle) {
            numHandlesWithJ += handleQty
            // do not count for J-Profile (only JProf has insertLinearFtCost)
            if (!masterHandle.insertLinearFtCost) {
              // count insert handles
              numHandles += handleQty

              // insert with ADDITIONAL linear ft summation to perimeter
              if (masterHandle?.insertAddLinearFt) {
                handlesPerimeterFt += masterHandle.insertAddLinearFt * handleQty
              }

              // what plykea charges client per unit priced handles
              let insertHandlesCost = masterHandle.costUS * handleQty
              materialHandlesCost += insertHandlesCost

              // unit cost for INSERT handles (and not J-Prof) - what jupitr charges us
              if (
                [
                  'semiGrab',
                  'midSemiGrab',
                  'lrgSemiGrab',
                  'halfArc',
                  'lrgHalfArc',
                  'quartArc',
                  'lrgQuartArc',
                  'semiCir',
                  'lrgSemiCir'
                ].includes(handle.value)
              ) {
                // split lucidHandlesCost; what Lucid charges plykea
                materialLucidHandlesCost += masterHandle.costUS * handleQty
              }
            }
          } else {
            if (legacyHandle === false) {
              o.notifications.push('Discontinued handle needs to be updated')
              legacyHandle = true
            }
          }

          // add linear ft cost for j-profile handle
          let insertLinearFt = null
          let insertHeightFt = null
          let handleAreaSqFt = null

          if (handle.value == 'j') {
            switch (handle.orientation) {
              case 'h':
                // horizontal j-profile
                insertLinearFt = width / mmPerFoot // in ft
                handleAreaSqFt = (width * masterHandle.insertHeightValue) / sqMmPerSqFoot // in sq ft
                break
              case 'v':
                // vertical j-profile
                insertLinearFt = height / mmPerFoot // in ft
                handleAreaSqFt = (height * masterHandle.insertHeightValue) / sqMmPerSqFoot // in sq ft
                break
            }

            let jHandlePerimeter = insertLinearFt * handleQty

            // split lucidHandlesCost to Lucid pricing; what Lucid charges Plykea
            materialLucidHandlesCost += jHandlePerimeter * masterHandle.insertLinearFtCost

            // jHandlesCost is what Plykea charges clients
            let jHandleCost =
              jHandlePerimeter * masterHandle.insertLinearFtCost * store.state.pricing.usMultiplier.jHandles.value
            materialHandlesCost += jHandleCost

            // add j-handles linear feet to total jHandles perimeter
            jHandlesPerimeterFt += jHandlePerimeter
            // add j-handles linear feet to TOTAL
            handlesPerimeterFt += (3 * insertLinearFt + 0.1) * handleQty
          } else if (handle.value == 'invisipull') {
            // add handles linear feet
            handlesPerimeterFt += (width / mmPerFoot + 0.2) * handleQty
          } else if (handle.value == 'invisipull-short') {
            // add handles linear feet
            handlesPerimeterFt += (width / mmPerFoot) * handleQty
          } else {
            insertLinearFt =
              masterHandle.insertWidthUnit == 'mm'
                ? masterHandle.insertWidthValue / mmPerFoot
                : (width / mmPerFoot) * masterHandle.insertWidthValue
            insertHeightFt =
              masterHandle.insertHeightUnit == 'mm'
                ? masterHandle.insertHeightValue / mmPerFoot
                : (height / mmPerFoot) * masterHandle.insertHeightValue
          }

          if (insertLinearFt && handleAreaSqFt) {
            // jProfile handle area
            handleArea += handleAreaSqFt * handleQty
          } else if (insertLinearFt && insertHeightFt) {
            // insert handle area
            handleArea += insertHeightFt * insertLinearFt * handleQty
          }
        }
      }

      // wastage
      let wastage = material.continuousGrain === true ? productData.usContinuousGrainWastage : productData.usWastage

      let m = (o[material.id] = {})
      m.name = material.colourCode ? material.colourCode : material.value
      m.thickness = material.thickness
      m.area = area
      m.areaWithWastage = m.area * wastage
      // perimeter here will be in mm (will switch back to ft when calculating totals)
      m.perimeter = handlesPerimeterFt * mmPerFoot + perimeter
      m.jHandlesPerimeterFt = jHandlesPerimeterFt // j-handle linear ft for Jupitr pricing list
      m.numHandles = numHandles // used for Jupitr pricing list
      m.sheetArea = material.w * material.h
      m.requiredSheets = m.areaWithWastage / m.sheetArea
      m.requiredSheetsRounded = Math.ceil(m.requiredSheets)
      m.itemCount = itemCount
      m.total = 0
      // Unused variables: numHandlesWithJ, handleArea (but stored to make sure they are not lost if needed)
      m.numHandlesWithJ = numHandlesWithJ
      m.handleArea = handleArea

      // adjust sheet usage for continuous grain (if sheet usage modulus is greate than 0.5 add another sheet)
      if (material.continuousGrain) {
        m.requiredSheetsRounded += Math.round(m.requiredSheets % 1)
      }

      // Handle cost is merged into the fronts (non-4mm) material — the 4mm insert
      // material is ordering-only and is NOT charged (Lucid does not bill for 4mm
      // sheets, so nor do we). See "hide US 4mm" model (commit 36e4a002).
      // lucidHandlesCost = what Lucid charges Plykea; handlesCost = what Plykea charges clients.
      m.lucidHandlesCost = material.thickness == 4 ? 0 : materialLucidHandlesCost
      m.handlesCost =
        material.thickness == 4 ? 0 : materialHandlesCost * store.state.pricing.usMultiplier.generalHandles.value

      // 4mm handle-insert material: sheet usage is shown for material ordering only, not charged
      if (material.thickness == 4) {
        m.cost = 0
        m.sheetCost = 0 // no 4mm material sheet cost
        m.programmingCost = 0
        m.cuttingCost = 0
        m.edgeFinishingCost = 0
        m.surfaceFinishingCost = 0
        m.packagingPerSqFt = 0
        m.total = 0
      }

      // 18mm fronts: base material price PLUS the merged handle cost
      else {
        let usMaterialPrice = MasterPricing.calculateUsPriceForMaterial(
          material,
          m.area,
          m.perimeter,
          m.requiredSheetsRounded
        )

        if (usMaterialPrice) {
          m.edgeBandingCost = edgeBandingCost
          m.sheetCost = usMaterialPrice.details.materialCost / m.requiredSheetsRounded
          m.programmingCost = usMaterialPrice.details.programmingCost
          m.cuttingCost = usMaterialPrice.details.cuttingCost
          m.edgeFinishingCost = usMaterialPrice.details.edgeFinishingCost
          m.surfaceFinishingCost = usMaterialPrice.details.surfaceFinishingCost
          m.packagingPerSqFt = usMaterialPrice.details.packagingPerSqFt
          // Merge handle cost into the fronts material:
          m.cost = utils.toFixedNumber(usMaterialPrice.cost + m.lucidHandlesCost, 2)
          m.total = utils.toFixedNumber(usMaterialPrice.total + m.edgeBandingCost + m.handlesCost, 2)
          o.details.push(usMaterialPrice.details)
        } else {
          // Pricing cannot be calculated — still preserve the handle charge so it isn't silently dropped
          m.cost = utils.toFixedNumber(m.lucidHandlesCost, 2)
          m.total = utils.toFixedNumber(m.handlesCost, 2)
          m.sheetCost = 0
          m.programmingCost = 0
          m.cuttingCost = 0
          m.edgeFinishingCost = 0
          m.surfaceFinishingCost = 0
          m.packagingPerSqFt = 0
        }
      }
      // console.log('Fronts material info', m)

      // add to total
      o.cost = utils.toFixedNumber(o.cost + m.cost, 2)
      o.total = utils.toFixedNumber(o.total + m.total, 2)
    }
    return o
  },

  worktops(o, version, notifications) {
    let skuWarning = false

    for (var i = 0; i < version.worktops.length; i++) {
      var w = version.worktops[i]
      if (w.thickness && w.material && w.size && w.faces) {
        const worktopMaterial = resolveWorktop(w, 'us')
        const worktop =
          worktopMaterial && worktopMaterial.sizes
            ? worktopMaterial.sizes.find((o) => {
                return o.thickness === w.thickness && o.sided === w.faces && o.size === w.size
              })
            : undefined
        if (!worktop) {
          o.incomplete = true
          continue
        }

        let cutCost = store.state.pricing.usWorktopCosts.usWorktopCutoutCost.value
        let finishCost = store.state.pricing.usWorktopCosts.usWorktopFinishCost.value
        let usWorktopMultiplier = store.state.pricing.usWorktopCosts.usWorktopMultiplier.value

        var cost = worktop.cost + cutCost + finishCost
        var total = cost * usWorktopMultiplier

        o.cost += cost
        o.total += total

        o.debug.items.push({
          code: w.thickness + '-' + w.material + '-' + w.size + '-' + w.faces,
          cost: cost,
          total: total,
          data: worktop
        })
      } else {
        o.incomplete = true
        if (!skuWarning) {
          notifications.push('Unable to create SKU for worktop. Check all fields are complete.')
          skuWarning = true
        }
      }
    }

    o.cost = utils.toFixedNumber(o.cost, 2)
    o.total = utils.toFixedNumber(o.total, 2)
  },

  urtils(urtils, materials, exchangeRate) {
    let o = {
      cost: 0,
      total: 0,
      glassDoorCost: 0,
      programmingCost: 0,
      cuttingCost: 0,
      edgeFinishingCost: 0,
      surfaceFinishingCost: 0,
      packaging: 0,
      packagingPerSqFt: 0,
      area: 0,
      perimeter: 0,
      incomplete: false,
      details: []
    }
    if (!urtils || urtils.length === 0 || !materials || materials.length === 0) {
      return o
    }

    materials.forEach((material) => {
      if (!material.value || !material.thickness || !material.w || !material.h) return

      let m = (o[material.id] = {
        id: material.id,
        thickness: material.thickness,
        areaWithWastage: 0,
        area: 0,
        perimeter: 0,
        itemCount: 0,
        doorPriced: false
      })

      // wastage
      let wastage = material.continuousGrain === true ? productData.usContinuousGrainWastage : productData.usWastage

      urtils.forEach((urtil) => {
        // check incomplete
        if (
          !urtil.type ||
          !urtil.carcass ||
          !urtil.back ||
          !urtil.w ||
          !urtil.d ||
          !urtil.h ||
          (urtil.type == 's' && !urtil.material1) ||
          (urtil.type == 't' && (!urtil.material1 || !urtil.material2)) ||
          (urtil.shelving && urtil.shelvingOptions.qty < 1)
        ) {
          o.incomplete = true
          return
        }

        // check incomplete by door coverage
        if (urtil.type == 's' && !urtil.doorCoverageOne) {
          o.incomplete = true
          return
        } else if (urtil.type == 't' && (!urtil.doorCoverageOne || !urtil.doorCoverageTwo)) {
          o.incomplete = true
          return
        }

        // Update Material Usage and costs
        // Carcass (priced using standard pricing algorithm)
        if (material.id === urtil.carcass) {
          let carcassArea = urtil.w * urtil.d * 2 + urtil.h * urtil.d * 2
          let perimeter

          // Calculate carcass perimeters
          let carcassPerimeter = (urtil.w + urtil.d) * 4 + (urtil.h + urtil.d) * 4
          let sidesRebatePerimeter = (urtil.h + 30) * 4 // 30mm is side rebate depth
          let basesRebatePerimeter = (urtil.w + 30) * 4 // 30mm is base rebate depth
          // let singleDoorGroovePerimeter = (urtil.w + 8) * 2 * 2 + (urtil.h + 8) * 2 * 2 // 5mm is door groove depth
          // let doorGroovePerimeter =
          //   urtil.type === 's' ? singleDoorGroovePerimeter : urtil.type === 't' ? singleDoorGroovePerimeter * 2 : 0
          let engravingPerimeter = urtil.trimming ? 20 * 12 : 20 * 8

          // add carcass perimeters to total perimeter

          // TEST: removed some perimeters to lower the price
          perimeter = carcassPerimeter + sidesRebatePerimeter + basesRebatePerimeter + engravingPerimeter

          // Calculate compartment divider cost (fins)
          // let finTslotPerimeter = (20 + urtil.d) * 2 + urtil.d * 2 // 20mm is fin T-slot width, 2x urtil depth mimics the t-slot lines
          if (urtil.compartments) {
            let finArea = urtil.h * urtil.d
            let finPerimeter = (urtil.h + urtil.d) * 2

            let numFins = urtil.compartments - 1

            carcassArea += finArea * numFins
            // perimeter += (finPerimeter + 2 * finTslotPerimeter) * numFins // TEST: removed for lower price
            perimeter += finPerimeter * numFins
            m.itemCount += numFins
          }

          if (urtil.shelving) {
            let shelfArea = urtil.w * urtil.d
            let shelfPerimeter = (urtil.w / urtil.compartments + urtil.d) * 2 * urtil.compartments

            let numShelves = urtil.shelvingOptions.qty
            // let isFixedShelves = urtil.shelvingOptions.type === 'fixed' // TEST: removed for lower price

            carcassArea += shelfArea * numShelves
            // perimeter += isFixedShelves
            perimeter += shelfPerimeter * numShelves
            m.itemCount += numShelves
          }

          // below is too inaccurate!!
          // // Factor carcass perimeter by 10%
          // perimeter *= 1.1 // 10% extra to match US script

          // Material cost for carcass
          let carcassAreaWithWastage = carcassArea * wastage

          // Update material usage
          m.areaWithWastage += carcassAreaWithWastage
          m.area += carcassArea
          m.perimeter += perimeter
          m.itemCount += 4 // 4 sides of the carcass (sides, back, top, bottom)
        }

        const doorAndBackWastage = 1.5
        // Back (priced as external costs * external markup)
        if (material.id === urtil.back) {
          let backArea = urtil.w * urtil.h // back is single sided
          let backAreaWithWastage = backArea * doorAndBackWastage // back wastage is 1.5x
          let backPerimeter = (urtil.w + urtil.h) * 2

          m.areaWithWastage += backAreaWithWastage
          m.area += backArea
          m.perimeter += backPerimeter
          m.itemCount += 1
        }

        // LEFT Doors
        if (
          (urtil.type === 's' && urtil.material1 && material.id === urtil.material1) ||
          (urtil.type === 't' && urtil.material2 && urtil.material1 !== urtil.material2)
        ) {
          // second condition is for twin doors where left door is a different material to the right. (rare)
          const leftDoorCost = calculateGlassDoorCost(material, urtil, 1)
          if (leftDoorCost > 0) {
            o.total += leftDoorCost
            o.glassDoorCost += leftDoorCost
            m.doorPriced = true
          }

          let leftDoorArea = (urtil.w / urtil.compartments) * urtil.doorCoverageOne * urtil.h
          let leftDoorAreaWithWastage = leftDoorArea * doorAndBackWastage
          let leftDoorPerimeter = ((urtil.w / urtil.compartments) * urtil.doorCoverageOne + urtil.h) * 2 + 2 * 3.15 * 30

          m.areaWithWastage += leftDoorAreaWithWastage
          m.area += leftDoorArea
          m.perimeter += leftDoorPerimeter
          m.itemCount += 1
        }

        // RIGHT Door - only if twin doors where right door is not the same material as left. (rare)
        if (urtil.type === 't' && urtil.material2 && urtil.material1 !== urtil.material2) {
          const rightDoorCost = calculateGlassDoorCost(material, urtil, 2)
          if (rightDoorCost > 0) {
            o.total += rightDoorCost
            o.glassDoorCost += rightDoorCost
            m.doorPriced = true
          }

          let rightDoorArea = (urtil.w / urtil.compartments) * urtil.doorCoverageOne * urtil.h
          let rightDoorAreaWithWastage = rightDoorArea * doorAndBackWastage
          let rightDoorPerimeter =
            ((urtil.w / urtil.compartments) * urtil.doorCoverageOne + urtil.h) * 2 + 2 * 3.15 * 30

          m.areaWithWastage += rightDoorAreaWithWastage
          m.area += rightDoorArea
          m.perimeter += rightDoorPerimeter
          m.itemCount += 1
        }

        // TWIN Doors
        if (
          urtil.type === 't' &&
          urtil.material2 &&
          urtil.material1 === urtil.material2 &&
          material.id === urtil.material1
        ) {
          // filter through o.details to see if there is already a twin door entry // shouldnt happen, just checking
          let existingTwinDoor = o.details.find((detail) => detail.type === 'twinDoor')
          if (!existingTwinDoor) {
            // twin doors where left and right door are the same material (most cases)
            const leftDoorCost = calculateGlassDoorCost(material, urtil, 1)
            const rightDoorCost = calculateGlassDoorCost(material, urtil, 2)
            if (leftDoorCost > 0 || rightDoorCost > 0) {
              o.total += leftDoorCost + rightDoorCost
              o.glassDoorCost += leftDoorCost + rightDoorCost
              m.doorPriced = true
            }

            let leftDoorArea = (urtil.w / urtil.compartments) * urtil.doorCoverageOne * urtil.h
            let rightDoorArea = (urtil.w / urtil.compartments) * urtil.doorCoverageTwo * urtil.h
            let twinDoorArea = leftDoorArea + rightDoorArea
            let twinDoorAreaWithWastage = twinDoorArea * doorAndBackWastage
            let leftDoorPerimeter =
              ((urtil.w / urtil.compartments) * urtil.doorCoverageOne + urtil.h) * 2 + 2 * 3.15 * 30
            let rightDoorPerimeter =
              ((urtil.w / urtil.compartments) * urtil.doorCoverageTwo + urtil.h) * 2 + 2 * 3.15 * 30
            let twinDoorPerimeter = leftDoorPerimeter + rightDoorPerimeter

            m.areaWithWastage += twinDoorAreaWithWastage
            m.area += twinDoorArea
            m.perimeter += twinDoorPerimeter
            m.itemCount += 2
          }
        }
      })

      if (m.id === material.id && m.thickness === 18) {
        // Calculate CARCASS Totals
        let requiredCarcassSheetsRounded = Math.ceil(m.areaWithWastage / (material.w * material.h)) // do this after all urtils
        const carcassMaterialPrice = MasterPricing.calculateUsPriceForMaterial(
          material,
          m.area,
          m.perimeter,
          requiredCarcassSheetsRounded
        )
        if (carcassMaterialPrice && carcassMaterialPrice.details) {
          o.details.push({ ...carcassMaterialPrice.details, type: 'carcass' })

          // Accumulate individual costs
          o.programmingCost += carcassMaterialPrice.details.programmingCost || 0
          o.cuttingCost += carcassMaterialPrice.details.cuttingCost || 0
          o.edgeFinishingCost += carcassMaterialPrice.details.edgeFinishingCost || 0
          o.surfaceFinishingCost += carcassMaterialPrice.details.surfaceFinishingCost || 0
          o.packagingPerSqFt += carcassMaterialPrice.details.packagingPerSqFt || 0
          o.area += carcassMaterialPrice.details.areaSqFt || 0
          o.perimeter += carcassMaterialPrice.details.perimeterFt || 0

          o.total += carcassMaterialPrice.total || 0
        }
      } else if (m.id === material.id && m.thickness !== 18 && !m.doorPriced) {
        // Calculate 6mm BACK and DOORS Totals
        let requiredBackAndDoorSheetsRounded = Math.ceil(m.areaWithWastage / (material.w * material.h)) // do this after all urtils
        const backAndDoorMaterialPrice = MasterPricing.calculateUsPriceForMaterial(
          material,
          m.area,
          m.perimeter,
          requiredBackAndDoorSheetsRounded
        )
        if (backAndDoorMaterialPrice && backAndDoorMaterialPrice.details) {
          o.details.push({ ...backAndDoorMaterialPrice.details, type: 'backAndDoor' })

          // Accumulate individual costs
          o.programmingCost += backAndDoorMaterialPrice.details.programmingCost || 0
          o.cuttingCost += backAndDoorMaterialPrice.details.cuttingCost || 0
          o.edgeFinishingCost += backAndDoorMaterialPrice.details.edgeFinishingCost || 0
          o.surfaceFinishingCost += backAndDoorMaterialPrice.details.surfaceFinishingCost || 0
          o.packagingPerSqFt += backAndDoorMaterialPrice.details.packagingPerSqFt || 0
          o.area += backAndDoorMaterialPrice.details.areaSqFt || 0
          o.perimeter += backAndDoorMaterialPrice.details.perimeterFt || 0

          o.total += backAndDoorMaterialPrice.total || 0
        }
      }
    })

    // Store fixed packaging cost
    o.packaging = utils.toFixedNumber(store.state.pricing.usCosts?.packAndCrateFixedCost?.value || 0, 2)

    // Sum up Lucid's internal cost (manufacturing costs, NOT material)
    o.glassDoorCost = utils.toFixedNumber(o.glassDoorCost, 2)
    o.cost = utils.toFixedNumber(
      o.programmingCost +
        o.cuttingCost +
        o.edgeFinishingCost +
        o.surfaceFinishingCost +
        o.packagingPerSqFt +
        o.packaging +
        o.glassDoorCost,
      2
    )

    // Apply markup for customer total
    const urtilMarkup = store.state.pricing.usMultiplier?.usUrtilMarkup?.value || 1
    o.total *= exchangeRate * urtilMarkup
    o.total = utils.toFixedNumber(o.total + o.packaging, 2)

    // Ensure area and perimeter are numbers
    o.area = utils.toFixedNumber(o.area || 0, 2)
    o.perimeter = utils.toFixedNumber(o.perimeter || 0, 2)

    return o
  }
}

function calculateGlassDoorCost(material, urtil, doorNum) {
  if (material.value !== 'clearGlass') return 0

  const doorWidth =
    doorNum === 1
      ? (urtil.w / urtil.compartments) * urtil.doorCoverageOne
      : (urtil.w / urtil.compartments) * urtil.doorCoverageTwo
  const doorHeight = urtil.h
  const areaM2 = (doorWidth * doorHeight) / 1000000
  return utils.toFixedNumber(56.31 + 100.72 * areaM2, 2)
}
